/**
 * DashboardPage — AB8 Beauty "back office" home, styled after the Fynix
 * dashboard reference in the brand's pink palette.
 *
 * Affiliates see: a pink referral-cash hero, KPI cards, a monthly earnings bar
 * chart (pink bars), referral codes, and recent commissions. Non-affiliates see
 * a clean join CTA. All commission rates are the 3% / 2% / 1% AB8 tiers.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, Tooltip, Cell,
  RadialBarChart, RadialBar, PolarAngleAxis,
} from 'recharts';
import DashboardLayout from '../components/dashboard/DashboardLayout';
import AmbassadorApplyTile from '../components/AmbassadorApplyTile';
import { useAuthCtx } from '../App';
import api from '../utils/api';
import { formatDate } from '../utils/helpers';
import { useCurrency } from '../hooks/useCurrency';
import { toast } from 'react-toastify';
import { Icon } from '../components/ui/Icon';

const PERIODS = [
  { key: 'today',    label: 'Today',    field: 'today_cents',    countField: 'today_count' },
  { key: 'week',     label: '7 days',   field: 'week_cents',     countField: 'week_count' },
  { key: 'month',    label: '30 days',  field: 'month_cents',    countField: 'month_count' },
  { key: 'all_time', label: 'All time', field: 'all_time_cents', countField: null },
];

const LEVEL_RATES = { 1: '3%', 2: '2%', 3: '1%' };
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

// Orders that represent real revenue (used for lifetime-spend + order count).
const REVENUE_STATUSES = ['paid', 'processing', 'shipped', 'delivered'];

// Light-theme status pill matching the dashboard's pink palette.
const ORDER_STATUS_STYLE = {
  delivered:  'bg-green-100 text-green-700',
  shipped:    'bg-blue-100 text-blue-700',
  processing: 'bg-amber-100 text-amber-700',
  paid:       'bg-tea-100 text-tea-700',
  pending:    'bg-gray-100 text-gray-500',
  cancelled:  'bg-red-100 text-red-600',
  refunded:   'bg-red-100 text-red-600',
};

/** Build a 12-month approved/pending series (in dollars) from recent commissions. */
function buildMonthly(recent = []) {
  const now = new Date();
  const series = Array.from({ length: 12 }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - (11 - i), 1);
    return { key: `${d.getFullYear()}-${d.getMonth()}`, month: MONTHS[d.getMonth()], approved: 0, pending: 0 };
  });
  const idx = Object.fromEntries(series.map((s, i) => [s.key, i]));
  for (const c of recent) {
    const d = new Date(c.createdAt);
    const k = `${d.getFullYear()}-${d.getMonth()}`;
    if (idx[k] == null) continue;
    const amt = (c.amountInCents || 0) / 100;
    if (c.status === 'pending') series[idx[k]].pending += amt;
    else if (c.status !== 'cancelled') series[idx[k]].approved += amt;
  }
  return series;
}

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-xl bg-white shadow-lg border border-tea-100 px-3 py-2 text-xs">
      <div className="font-bold text-gray-900 mb-1">{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center gap-2 text-gray-600">
          <span className="inline-block h-2 w-2 rounded-full" style={{ background: p.color }} />
          <span className="capitalize">{p.dataKey}</span>
          <span className="ml-auto font-semibold text-gray-900">${p.value.toFixed(0)}</span>
        </div>
      ))}
    </div>
  );
}

export default function DashboardPage() {
  const { user } = useAuthCtx();
  const { fmt } = useCurrency();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('all_time');
  const [newCode, setNewCode] = useState('');
  const [orders, setOrders] = useState([]);
  const [ordersTotal, setOrdersTotal] = useState(0);

  const isAffiliate = ['affiliate', 'admin', 'ambassador', 'vip'].includes(user?.role);

  useEffect(() => {
    if (!isAffiliate) { setLoading(false); return; }
    api.get('/affiliates/dashboard')
      .then(res => setData(res.data))
      .catch(() => toast.error('Failed to load dashboard'))
      .finally(() => setLoading(false));
  }, [isAffiliate]);

  // Purchase history + Glow Cash rewards — for every signed-in user.
  useEffect(() => {
    api.get('/orders?limit=100')
      .then(res => {
        setOrders(res.data.data || []);
        setOrdersTotal(res.data.pagination?.total ?? (res.data.data || []).length);
      })
      .catch(() => { /* leave purchase history empty on failure */ });
  }, []);

  // Glow Cash earned across all orders, and lifetime spend on revenue orders.
  const glowEarnedCents = useMemo(
    () => orders.reduce((sum, o) => sum + (o.rewardEarnedInCents || 0), 0),
    [orders],
  );
  const lifetimeSpendCents = useMemo(
    () => orders.filter(o => REVENUE_STATUSES.includes(o.status))
                .reduce((sum, o) => sum + (o.totalInCents || 0), 0),
    [orders],
  );

  const periodTotal = useMemo(() => {
    const p = PERIODS.find(x => x.key === period);
    return { cents: parseInt(data?.period?.totals?.[p.field] || 0), label: p.label };
  }, [data, period]);

  const monthly = useMemo(() => buildMonthly(data?.recentCommissions || []), [data]);
  const byLevel = (data?.period?.byLevel || []).reduce((a, r) => {
    a[r.level] = { count: parseInt(r.count), amount: parseInt(r.amount_cents) }; return a;
  }, {});

  const copyLink = (code) => {
    navigator.clipboard.writeText(`${window.location.origin}/register?ref=${code}`);
    toast.success('Referral link copied!');
  };
  const createCode = async () => {
    try {
      const res = await api.post('/affiliates/codes', { label: 'New campaign', code: newCode || undefined });
      toast.success('Code created: ' + res.data.code);
      setData(prev => ({ ...prev, referralCodes: [...(prev.referralCodes || []), res.data] }));
      setNewCode('');
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to create code'); }
  };
  const upgrade = async () => {
    try { await api.post('/affiliates/upgrade'); toast.success("Welcome to the referral program!"); window.location.reload(); }
    catch { toast.error('Upgrade failed — please try again.'); }
  };

  const subtitle = 'Track your glow rewards with AB8 insights and recommendations.';

  // ── Non-affiliate join view ───────────────────────────────────────────────
  if (!isAffiliate) {
    return (
      <DashboardLayout title={`Welcome, ${user?.firstName || ''}`} subtitle={subtitle}>
        <AmbassadorApplyTile className="mb-5" />
        <div className="grid md:grid-cols-3 gap-5 mt-1">
          <div className="md:col-span-2 rounded-3xl p-7 text-white relative overflow-hidden"
               style={{ background: 'linear-gradient(135deg,#ec4899 0%,#be185d 55%,#7a1746 100%)' }}>
            <div className="absolute -right-10 -top-10 h-48 w-48 rounded-full bg-white/15 blur-2xl" />
            <h2 className="text-2xl font-extrabold relative">Unlock 3-tier referral cash</h2>
            <p className="text-white/85 text-sm mt-2 max-w-md relative">
              Earn <strong>3% / 2% / 1%</strong> across three referral levels — paid as cash you redeem at checkout.
            </p>
            <button onClick={upgrade} className="mt-5 bg-white text-tea-700 font-bold rounded-full px-6 py-3 text-sm relative">
              Join the program
            </button>
          </div>
          <Card>
            <Kicker>
              <span className="inline-flex items-center gap-1.5">
                <Icon name="commissions" className="w-4 h-4 text-pink-600" />
                Referral cash
              </span>
            </Kicker>
            <div className="text-4xl font-extrabold text-tea-700 mt-1">{fmt(user?.walletBalanceCents || 0)}</div>
            <p className="text-xs text-gray-400 mt-2">Redeems 1:1 at checkout.</p>
            <Link to="/shop" className="block text-center mt-4 rounded-full bg-tea-600 text-white font-semibold text-sm py-2.5">Shop now</Link>
          </Card>
        </div>

        {/* Purchase history + Glow Cash rewards */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-5 mt-5 mb-2">
          <PurchaseHistoryCard className="xl:col-span-2" fmt={fmt} orders={orders} lifetimeSpendCents={lifetimeSpendCents} />
          <RewardsEarnedCard
            fmt={fmt}
            redeemableCents={user?.walletBalanceCents || 0}
            glowEarnedCents={glowEarnedCents}
            ordersCount={ordersTotal}
          />
        </div>
      </DashboardLayout>
    );
  }

  if (loading) {
    return <DashboardLayout title="Home" subtitle={subtitle}>
      <div className="py-24 text-center text-tea-600">Loading your dashboard…</div>
    </DashboardLayout>;
  }

  const wallet = data?.walletBalanceCents || 0;
  const totalEarned = parseInt(data?.commissions?.total_earned_cents || 0);
  const pending = parseInt(data?.commissions?.pending_cents || 0);
  const downline = data?.downlineCount || 0;
  // Referral score: share of approved vs total earned, scaled to a friendly %.
  const score = totalEarned > 0 ? Math.min(100, Math.round(((totalEarned - pending) / totalEarned) * 100)) : 92;
  const scoreData = [{ name: 'score', value: score, fill: '#db2777' }];

  return (
    <DashboardLayout title="Home" subtitle={subtitle}>
      <AmbassadorApplyTile className="mb-5" />
      {/* ── Row 1: balance + KPIs + score ─────────────────────────────── */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-5 mt-1">
        {/* Referral cash hero (pink gradient) */}
        <div className="rounded-3xl p-6 text-white relative overflow-hidden min-h-[200px] flex flex-col"
             style={{ background: 'linear-gradient(135deg,#f472b6 0%,#db2777 45%,#7a1746 100%)' }}>
          <div className="absolute -right-12 -bottom-12 h-56 w-56 rounded-full border-[18px] border-white/10" />
          <div className="absolute right-6 top-6 h-9 w-9 grid place-items-center rounded-full bg-white/25 text-white text-lg">+</div>
          <div className="text-sm text-white/80 font-medium">Referral cash</div>
          <div className="text-4xl font-extrabold mt-1">{fmt(wallet)} <span className="text-base font-semibold text-white/70">USD</span></div>
          <div className="mt-auto flex gap-3 pt-6 relative">
            <Link to="/shop" className="flex-1 text-center bg-white/95 text-tea-700 font-bold rounded-full py-2.5 text-sm">Spend ↓</Link>
            <button onClick={() => copyLink(data?.referralCodes?.[0]?.code || '')}
              className="flex-1 bg-black/30 text-white font-bold rounded-full py-2.5 text-sm">Share ↗</button>
          </div>
        </div>

        {/* KPI mini-cards (like AI Enhancements) */}
        <Card>
          <div className="flex items-center justify-between">
            <span className="font-bold text-gray-900">Earnings</span>
            <span className="text-[11px] text-gray-400">{periodTotal.label}</span>
          </div>
          <div className="grid grid-cols-1 gap-3 mt-4">
            <MiniStat icon="↑" tint="bg-tea-50 text-tea-700" label="Total earned" value={fmt(totalEarned)} />
            <MiniStat icon={<Icon name="clock" className="w-4 h-4" />} tint="bg-amber-50 text-amber-700" label="Pending" value={fmt(pending)} />
            <MiniStat icon={<Icon name="users" className="w-4 h-4" />} tint="bg-fuchsia-50 text-fuchsia-700" label="Direct downline" value={downline} />
          </div>
        </Card>

        {/* Referral score (like Finance Score) */}
        <Card>
          <div className="flex items-center justify-between">
            <span className="font-bold text-gray-900">Referral Score</span>
            <span className="text-gray-300">···</span>
          </div>
          <div className="flex items-center gap-4 mt-2">
            <div className="relative h-28 w-28">
              <ResponsiveContainer>
                <RadialBarChart innerRadius="72%" outerRadius="100%" data={scoreData} startAngle={90} endAngle={-270}>
                  <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
                  <RadialBar dataKey="value" cornerRadius={20} background={{ fill: '#fce7f3' }} />
                </RadialBarChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 grid place-items-center">
                <span className="text-2xl font-extrabold text-gray-900">{score}%</span>
              </div>
            </div>
            <div>
              <div className="text-xs text-gray-400">Reward quality</div>
              <div className="text-2xl font-extrabold text-tea-700">{score >= 80 ? 'Excellent' : score >= 50 ? 'Good' : 'Building'}</div>
              <div className="text-xs text-gray-400 mt-1 capitalize">{user?.tier || 'standard'} tier</div>
            </div>
          </div>
        </Card>
      </div>

      {/* ── Row 2: cashflow chart + tier rates ────────────────────────── */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-5 mt-5">
        <Card className="xl:col-span-2">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <span className="font-bold text-gray-900">Earnings flow</span>
              <div className="text-2xl font-extrabold text-gray-900 mt-1">{fmt(totalEarned)}</div>
            </div>
            <div className="flex items-center gap-4 text-xs">
              <Legend color="#7a1746" label="Approved" />
              <Legend color="#f9a8d0" label="Pending" />
              <div role="tablist" className="inline-flex bg-gray-100 rounded-full p-1 ml-2">
                {PERIODS.map(p => (
                  <button key={p.key} onClick={() => setPeriod(p.key)}
                    className={'px-2.5 py-1 text-[11px] font-semibold rounded-full transition-colors ' +
                      (period === p.key ? 'bg-white text-tea-700 shadow-sm' : 'text-gray-500')}>{p.label}</button>
                ))}
              </div>
            </div>
          </div>
          <div className="h-64 mt-4">
            <ResponsiveContainer>
              <BarChart data={monthly} barCategoryGap="28%">
                <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#9ca3af' }} />
                <Tooltip cursor={{ fill: 'rgba(236,72,153,0.06)' }} content={<ChartTooltip />} />
                <Bar dataKey="approved" stackId="a" fill="#7a1746" radius={[0, 0, 0, 0]} />
                <Bar dataKey="pending" stackId="a" fill="#f9a8d0" radius={[6, 6, 0, 0]}>
                  {monthly.map((_, i) => <Cell key={i} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* Tier rates */}
        <Card>
          <span className="font-bold text-gray-900">Your reward tiers</span>
          <p className="text-xs text-gray-400 mt-1">Cash on every order, three levels deep.</p>
          <div className="space-y-3 mt-4">
            {[1, 2, 3].map(l => (
              <div key={l} className="flex items-center gap-3 rounded-2xl border border-tea-100 p-3">
                <span className="grid place-items-center h-10 w-10 rounded-xl bg-tea-50 text-tea-700 font-extrabold">{LEVEL_RATES[l]}</span>
                <div className="flex-1">
                  <div className="text-sm font-semibold text-gray-900">Level {l}</div>
                  <div className="text-[11px] text-gray-400">
                    {l === 1 ? 'People you refer' : l === 2 ? 'Their referrals' : 'Third level'}
                  </div>
                </div>
                <div className="text-sm font-bold text-gray-900">{fmt(byLevel[l]?.amount || 0)}</div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* ── Row 3: recent commissions + referral codes ────────────────── */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-5 mt-5 mb-2">
        <Card className="xl:col-span-2">
          <span className="font-bold text-gray-900">Recent commissions</span>
          <div className="overflow-x-auto mt-4">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-400 text-xs border-b border-gray-100">
                  <th className="pb-2 font-medium">Amount</th><th className="pb-2 font-medium">Level</th>
                  <th className="pb-2 font-medium">Date</th><th className="pb-2 font-medium text-right">Status</th>
                </tr>
              </thead>
              <tbody>
                {(data?.recentCommissions || []).length === 0 && (
                  <tr><td colSpan={4} className="py-8 text-center text-gray-400">Share your link to start earning.</td></tr>
                )}
                {(data?.recentCommissions || []).slice(0, 7).map(c => (
                  <tr key={c.id} className="border-b border-gray-50 last:border-0">
                    <td className="py-3 font-bold text-gray-900">{fmt(c.amountInCents)}</td>
                    <td className="py-3 text-gray-500">L{c.level} · {Math.round(parseFloat(c.rate) * 100)}%</td>
                    <td className="py-3 text-gray-500">{formatDate(c.createdAt)}</td>
                    <td className="py-3 text-right">
                      <span className={'text-[10px] font-bold uppercase tracking-wide rounded-full px-2.5 py-1 ' +
                        (c.status === 'paid' ? 'bg-green-100 text-green-700'
                         : c.status === 'approved' ? 'bg-tea-100 text-tea-700'
                         : c.status === 'pending' ? 'bg-amber-100 text-amber-700'
                         : 'bg-gray-100 text-gray-500')}>{c.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        <Card>
          <div className="flex items-center justify-between">
            <span className="font-bold text-gray-900">Referral codes</span>
            <span className="text-[11px] text-gray-400">{(data?.referralCodes || []).length} active</span>
          </div>
          <ul className="space-y-2 mt-4">
            {(data?.referralCodes || []).slice(0, 4).map(c => (
              <li key={c.id} className="flex items-center gap-2 rounded-2xl border border-tea-100 px-3 py-2.5">
                <span className="font-mono font-bold text-tea-700 text-sm">{c.code}</span>
                <span className="text-[11px] text-gray-400 ml-1 truncate">{c.label}</span>
                <button onClick={() => copyLink(c.code)} className="ml-auto text-xs font-semibold text-tea-600 hover:underline">Copy</button>
              </li>
            ))}
            {(data?.referralCodes || []).length === 0 && (
              <li className="text-sm text-gray-400 text-center py-4">No codes yet.</li>
            )}
          </ul>
          <div className="flex gap-2 mt-4">
            <input value={newCode} onChange={e => setNewCode(e.target.value)} placeholder="Custom code"
              maxLength={30} className="flex-1 text-sm rounded-full border border-gray-200 px-4 py-2 outline-none focus:border-tea-400" />
            <button onClick={createCode} className="rounded-full bg-tea-600 text-white text-sm font-semibold px-4">Create</button>
          </div>
        </Card>
      </div>

      {/* ── Row 4: purchase history + Glow Cash rewards ───────────────── */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-5 mt-5 mb-2">
        <PurchaseHistoryCard className="xl:col-span-2" fmt={fmt} orders={orders} lifetimeSpendCents={lifetimeSpendCents} />
        <RewardsEarnedCard
          fmt={fmt}
          redeemableCents={wallet}
          glowEarnedCents={glowEarnedCents}
          ordersCount={ordersTotal}
        />
      </div>
    </DashboardLayout>
  );
}

/* ── Small presentational helpers ─────────────────────────────────────────── */
function Card({ children, className = '' }) {
  return <div className={'bg-white rounded-3xl p-5 shadow-[0_6px_22px_rgba(190,24,93,0.06)] ' + className}>{children}</div>;
}
function Kicker({ children }) {
  return <div className="text-xs uppercase tracking-wide text-gray-400 font-semibold">{children}</div>;
}
function MiniStat({ icon, tint, label, value }) {
  return (
    <div className="flex items-center gap-3 rounded-2xl border border-gray-100 px-3 py-2.5">
      <span className={'grid place-items-center h-9 w-9 rounded-xl text-sm ' + tint}>{icon}</span>
      <div className="min-w-0">
        <div className="text-[11px] text-gray-400">{label}</div>
        <div className="text-base font-extrabold text-gray-900 truncate">{value}</div>
      </div>
    </div>
  );
}
function Legend({ color, label }) {
  return <span className="flex items-center gap-1.5 text-gray-500"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: color }} />{label}</span>;
}

/**
 * RewardsEarnedCard — the shopper's "rewards" snapshot: their redeemable wallet
 * balance plus the lifetime Glow Cash (1% self-cashback) they've banked.
 */
function RewardsEarnedCard({ fmt, redeemableCents, glowEarnedCents, ordersCount }) {
  return (
    <Card>
      <div className="flex items-center justify-between">
        <span className="font-bold text-gray-900">Rewards earned</span>
        <span className="text-[11px] text-gray-400">Glow Cash · 1% back</span>
      </div>
      <div className="mt-4 rounded-2xl p-4 text-white relative overflow-hidden"
           style={{ background: 'linear-gradient(135deg,#f472b6 0%,#db2777 55%,#9d174d 100%)' }}>
        <div className="absolute -right-8 -bottom-8 h-32 w-32 rounded-full bg-white/15 blur-xl" />
        <div className="text-xs text-white/80 font-medium relative">Redeemable balance</div>
        <div className="text-3xl font-extrabold mt-0.5 relative">{fmt(redeemableCents)}</div>
        <Link to="/shop" className="relative inline-block mt-3 bg-white/95 text-tea-700 font-bold rounded-full px-4 py-1.5 text-xs">
          Redeem at checkout →
        </Link>
      </div>
      <div className="grid grid-cols-2 gap-3 mt-3">
        <MiniStat icon={<Icon name="sparkles" className="w-4 h-4" />} tint="bg-fuchsia-50 text-fuchsia-700" label="Glow Cash earned" value={fmt(glowEarnedCents)} />
        <MiniStat icon={<Icon name="orders" className="w-4 h-4" />} tint="bg-tea-50 text-tea-700" label="Orders placed" value={ordersCount} />
      </div>
      <p className="text-[11px] text-gray-400 mt-3">
        You earn <strong className="text-tea-700">1% Glow Cash</strong> on every order — it lands here and redeems 1:1 at checkout.
      </p>
    </Card>
  );
}

/**
 * PurchaseHistoryCard — a compact recent-orders table that links out to the
 * full order history (with tracking + messages) on the Account page.
 */
function PurchaseHistoryCard({ fmt, orders, lifetimeSpendCents = 0, className = '' }) {
  return (
    <Card className={className}>
      <div className="flex items-center justify-between">
        <div>
          <span className="font-bold text-gray-900">Purchase history</span>
          {lifetimeSpendCents > 0 && (
            <div className="text-[11px] text-gray-400 mt-0.5">{fmt(lifetimeSpendCents)} spent all-time</div>
          )}
        </div>
        <Link to="/account" className="text-xs font-semibold text-tea-600 hover:underline">View all →</Link>
      </div>
      <div className="overflow-x-auto mt-4">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-gray-400 text-xs border-b border-gray-100">
              <th className="pb-2 font-medium">Order</th>
              <th className="pb-2 font-medium">Date</th>
              <th className="pb-2 font-medium">Total</th>
              <th className="pb-2 font-medium">Glow Cash</th>
              <th className="pb-2 font-medium text-right">Status</th>
            </tr>
          </thead>
          <tbody>
            {orders.length === 0 && (
              <tr><td colSpan={5} className="py-8 text-center text-gray-400">
                No orders yet — <Link to="/shop" className="text-tea-600 font-semibold hover:underline">start shopping</Link>.
              </td></tr>
            )}
            {orders.slice(0, 6).map(o => {
              const names = (o.items || [])
                .map(it => it.variant?.product?.name).filter(Boolean);
              const title = names.length ? names.join(', ') : 'Order';
              return (
                <tr key={o.id} className="border-b border-gray-50 last:border-0 align-top">
                  <td className="py-3 pr-3">
                    <div className="font-semibold text-gray-900 truncate max-w-[220px]">{title}</div>
                    <div className="text-[11px] text-gray-400 font-mono">#{o.id.slice(0, 8).toUpperCase()}</div>
                  </td>
                  <td className="py-3 text-gray-500 whitespace-nowrap">{formatDate(o.createdAt)}</td>
                  <td className="py-3 font-bold text-gray-900 whitespace-nowrap">{fmt(o.totalInCents)}</td>
                  <td className="py-3 whitespace-nowrap">
                    {o.rewardEarnedInCents > 0
                      ? <span className="font-semibold text-fuchsia-600">+{fmt(o.rewardEarnedInCents)}</span>
                      : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="py-3 text-right">
                    <span className={'text-[10px] font-bold uppercase tracking-wide rounded-full px-2.5 py-1 ' +
                      (ORDER_STATUS_STYLE[o.status] || 'bg-gray-100 text-gray-500')}>
                      {o.status}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
